/**
 * Ускорение холодного старта Mini App (особенно на медленной мобильной сети).
 *
 * 1. Вход по initData запускается сразу при загрузке модуля, ещё до первой отрисовки React.
 * 2. Параллельно со входом загружаются публичные данные экрана, который откроется первым
 *    (лента «Свежие» на главной или карточка объявления по ссылке), на языке прошлого запуска
 *    (при первом запуске — по языку Telegram), и словарь этого языка.
 *    Эти данные кладутся в кэш как «устаревшие»: экран показывает их сразу, а затем перезапрашивает
 *    с токеном (поля, зависящие от пользователя, — «в избранном», «моё объявление» — обновятся).
 *    Если язык не угадан — запрос пропадает впустую; если данные уже есть в кэше — они не перезаписываются.
 */
import { LOCALES, localeFromTelegram, parseStartParam, type Locale } from '@qm/shared';
import type { QueryClient } from '@tanstack/react-query';
import { api, qs, type ListingCardData, type ListingDetail } from './api';
import { ensureLocale } from '../i18n';
import { searchToQuery } from './queries';
import { getStartParam, getTelegramLanguage } from './telegram';

const LOCALE_KEY = 'qm.locale';

export function rememberLocale(locale: string) {
  try {
    localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* хранилище может быть недоступно — это только ускорение */
  }
}

function lastLocale(): Locale | null {
  try {
    const v = localStorage.getItem(LOCALE_KEY);
    return v && (LOCALES as readonly string[]).includes(v) ? (v as Locale) : null;
  } catch {
    return null;
  }
}

/** Должен совпадать с ключами и параметрами useSearch/useListing (lib/queries.ts). */
export const HOME_FRESH_PARAMS = { sort: 'newest' as const };
export const HOME_FRESH_LIMIT = 6;

export function startEarlyPrefetch(qc: QueryClient, path = location.pathname): void {
  // Язык прошлого запуска, а при первом запуске — тот же, что сервер выберет по языку Telegram.
  const locale = lastLocale() ?? localeFromTelegram(getTelegramLanguage());
  void ensureLocale(locale).catch(() => undefined); // словарь языка — тоже параллельно со входом
  const put = (key: unknown[], data: unknown) => {
    if (qc.getQueryData(key) !== undefined) return; // уже есть свежие данные (запрос с токеном успел раньше)
    qc.setQueryData(key, data, { updatedAt: 1 }); // «давно устарели» → экран сразу перезапросит с токеном
  };
  const fromLink = parseStartParam(getStartParam());
  const m = /^\/listing\/(\d{1,19})$/.exec(path);
  const listingId = m?.[1] ?? (path === '/' && fromLink.type === 'listing' ? fromLink.listingId : null);
  if (listingId) {
    void api<ListingDetail>(`/listings/${listingId}${qs({ locale })}`)
      .then((d) => put(['listing', listingId, locale], d))
      .catch(() => undefined);
    return;
  }
  if (path === '/') {
    void api<{ items: ListingCardData[]; nextCursor: string | null }>(`/listings${searchToQuery(HOME_FRESH_PARAMS, null, HOME_FRESH_LIMIT)}&locale=${locale}`)
      .then((p) => put(['search', HOME_FRESH_PARAMS, locale, HOME_FRESH_LIMIT], { pages: [p], pageParams: [null] }))
      .catch(() => undefined);
  }
}
